import type { WorkspaceApp } from 'src/api/facade/types'

export type AppProductType = 'built_in' | 'custom'
export type AppSolutionArea =
  | 'crm'
  | 'hrm'
  | 'identity'
  | 'finance'
  | 'operations'
  | 'custom'
export type AppAccessModel = 'workspace_roles' | 'app_roles'
export type AppProvisioningMode = 'manual' | 'jit' | 'scim'
export type AppAuthMode = 'workspace_core' | 'dedicated_sso'
export type AppApiExposure = 'internal' | 'partner' | 'public'

export type AppProductConfig = {
  productType: AppProductType
  solutionArea: AppSolutionArea
  accessModel: AppAccessModel
  provisioningMode: AppProvisioningMode
  authMode: AppAuthMode
  apiExposure: AppApiExposure
}

type AppProductConfigSource = Pick<
  WorkspaceApp,
  'id' | 'name' | 'description' | 'instanceTypeName' | 'isPublic' | 'settings'
>

const STORAGE_KEY_PREFIX = 'workspace-app-product-config'
const SETTINGS_KEY = 'platform_core'

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null

const isProductType = (value: unknown): value is AppProductType =>
  value === 'built_in' || value === 'custom'

const isSolutionArea = (value: unknown): value is AppSolutionArea =>
  value === 'crm' ||
  value === 'hrm' ||
  value === 'identity' ||
  value === 'finance' ||
  value === 'operations' ||
  value === 'custom'

const isAccessModel = (value: unknown): value is AppAccessModel =>
  value === 'workspace_roles' || value === 'app_roles'

const isProvisioningMode = (value: unknown): value is AppProvisioningMode =>
  value === 'manual' || value === 'jit' || value === 'scim'

const isAuthMode = (value: unknown): value is AppAuthMode =>
  value === 'workspace_core' || value === 'dedicated_sso'

const isApiExposure = (value: unknown): value is AppApiExposure =>
  value === 'internal' || value === 'partner' || value === 'public'

const parseAppProductConfig = (value: unknown): AppProductConfig | null => {
  if (!isRecord(value)) {
    return null
  }

  const {
    productType,
    solutionArea,
    accessModel,
    provisioningMode,
    authMode,
    apiExposure
  } = value

  if (
    !isProductType(productType) ||
    !isSolutionArea(solutionArea) ||
    !isAccessModel(accessModel) ||
    !isProvisioningMode(provisioningMode) ||
    !isAuthMode(authMode) ||
    !isApiExposure(apiExposure)
  ) {
    return null
  }

  return {
    productType,
    solutionArea,
    accessModel,
    provisioningMode,
    authMode,
    apiExposure
  }
}

const parseStoredAppProductConfig = (
  value: string
): AppProductConfig | null => {
  try {
    return parseAppProductConfig(JSON.parse(value))
  } catch (error) {
    if (error instanceof SyntaxError) {
      return null
    }

    throw error
  }
}

const inferSolutionArea = (
  app: Pick<WorkspaceApp, 'name' | 'description' | 'instanceTypeName'>
): AppSolutionArea => {
  const haystack = [app.name, app.description, app.instanceTypeName]
    .filter(Boolean)
    .join(' ')
    .toLowerCase()

  if (haystack.includes('crm') || haystack.includes('sales')) {
    return 'crm'
  }
  if (
    haystack.includes('hrm') ||
    haystack.includes('human resource') ||
    haystack.includes('people ops')
  ) {
    return 'hrm'
  }
  if (
    haystack.includes('identity') ||
    haystack.includes('auth') ||
    haystack.includes('sso')
  ) {
    return 'identity'
  }
  if (
    haystack.includes('billing') ||
    haystack.includes('payment') ||
    haystack.includes('finance')
  ) {
    return 'finance'
  }
  if (
    haystack.includes('ops') ||
    haystack.includes('support') ||
    haystack.includes('admin')
  ) {
    return 'operations'
  }

  return 'custom'
}

export const createDefaultAppProductConfig = (
  app: Pick<
    WorkspaceApp,
    'name' | 'description' | 'instanceTypeName' | 'isPublic'
  >
): AppProductConfig => {
  const solutionArea = inferSolutionArea(app)
  const isBuiltIn = solutionArea === 'crm' || solutionArea === 'hrm'

  return {
    productType: isBuiltIn ? 'built_in' : 'custom',
    solutionArea,
    accessModel: isBuiltIn ? 'workspace_roles' : 'app_roles',
    provisioningMode:
      solutionArea === 'identity'
        ? 'jit'
        : solutionArea === 'hrm'
          ? 'scim'
          : 'manual',
    authMode: 'workspace_core',
    apiExposure: app.isPublic
      ? 'public'
      : solutionArea === 'identity'
        ? 'partner'
        : 'internal'
  }
}

const getStorageKey = (appId: string) => `${STORAGE_KEY_PREFIX}:${appId}`

export const saveAppProductConfig = (
  appId: string,
  config: AppProductConfig
) => {
  if (typeof window === 'undefined') {
    return
  }

  window.localStorage.setItem(getStorageKey(appId), JSON.stringify(config))
}

export const readAppProductConfig = (
  app: AppProductConfigSource
): AppProductConfig => {
  const settingsConfig = parseAppProductConfig(
    isRecord(app.settings) ? app.settings[SETTINGS_KEY] : undefined
  )

  if (settingsConfig) {
    return settingsConfig
  }

  if (typeof window !== 'undefined') {
    const raw = window.localStorage.getItem(getStorageKey(app.id))
    if (raw) {
      const parsed = parseStoredAppProductConfig(raw)
      if (parsed) {
        return parsed
      }
    }
  }

  return createDefaultAppProductConfig(app)
}

export const appConfigurationIsReady = (
  app: Pick<WorkspaceApp, 'domain' | 'instanceTypeName'>,
  config: AppProductConfig
) =>
  Boolean(
    app.domain &&
    app.instanceTypeName &&
    config.productType &&
    config.solutionArea &&
    config.accessModel &&
    config.provisioningMode &&
    config.authMode &&
    config.apiExposure
  )
